import React, { useEffect, useRef } from 'react';
import { ArkoBackgroundAnalysisChip } from './ArkoBackgroundAnalysisChip';
import { usePortalBackgroundJobs } from './usePortalBackgroundJobs';
import {
  appendStoredArkoThreadMessages,
  ensureStoredActiveArkoThreadId,
  readStoredArkoThreadMessages,
} from './portalArkoThreadStorage';
import { DIKTAT_ANALYSIS_EXTRACT_SOURCE } from './portalDiktatFlow';

export type DiktatAnalysisStart = {
  documentId: string;
  documentName: string;
  documentContext: string;
  extractRawChars: number;
};

type DiktatAnalysisIslandProps = {
  customerId: string;
  webhookUrl: string;
  extraBody?: Record<string, unknown>;
  start: DiktatAnalysisStart | null;
  onConsumed: () => void;
  onQueueFull?: (hint: string) => void;
};

/**
 * Bestehende Hintergrundanalyse (frisches Fenster, Chunks, Popover, Handshake)
 * an das Diktat-Transkript — kein zweites Analyse-Produkt.
 */
export const DiktatAnalysisIsland: React.FC<DiktatAnalysisIslandProps> = ({
  customerId,
  webhookUrl,
  extraBody,
  start,
  onConsumed,
  onQueueFull,
}) => {
  const consumedKeyRef = useRef<string | null>(null);

  const {
    analysisViews,
    openJobId,
    setOpenJobId,
    canEnqueue,
    enqueueJob,
    cancelJob,
    dismissFinished,
  } = usePortalBackgroundJobs({
    customerId,
    webhookUrl,
    timeoutMs: 180_000,
    mode: 'anwalts_arko',
    extraBody,
    getThreadMessages: (threadId) => readStoredArkoThreadMessages(customerId, threadId),
    appendToThread: (threadId, updater) =>
      appendStoredArkoThreadMessages(customerId, threadId, updater),
  });

  useEffect(() => {
    if (!start) return;
    const key = `${start.documentId}:${start.extractRawChars}:${start.documentName}`;
    if (consumedKeyRef.current === key) return;
    if (!canEnqueue) {
      onQueueFull?.(
        'Analyse-Warteschlange voll (max. 2). Bitte eine laufende Analyse beenden — kein Fake-Lauf.',
      );
      onConsumed();
      return;
    }
    const threadId = ensureStoredActiveArkoThreadId(customerId);
    enqueueJob({
      documentId: start.documentId,
      documentName: start.documentName,
      threadId,
      documentContext: start.documentContext,
      extractRawChars: start.extractRawChars,
      extractSource: DIKTAT_ANALYSIS_EXTRACT_SOURCE,
    });
    consumedKeyRef.current = key;
    onConsumed();
  }, [start, canEnqueue, customerId, enqueueJob, onConsumed, onQueueFull]);

  if (analysisViews.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="portal-diktat-analysis-island">
      {analysisViews.map((job) => (
        <ArkoBackgroundAnalysisChip
          key={job.request_id}
          job={job}
          open={openJobId === job.request_id}
          onOpenChange={(open) => setOpenJobId(open ? job.request_id : null)}
          onCancel={cancelJob}
          onDismiss={dismissFinished}
        />
      ))}
    </div>
  );
};
